"use client"

import { createContext, use, useEffect, useState } from "react"
import { app, FrameContexts } from "@microsoft/teams-js"
import Cookies from "js-cookie"

// Until detection settles the state is provisional: it reads as "not in Teams"
// so render-only callers can paint the web layout immediately, while callers
// doing one-shot work (redirects, auth popups, notifySuccess, default-route
// selection) must wait for `isTeamsResolved`.
type TeamsContextState =
  | {
      isTeamsResolved: false
      isInTeams: false
      teamsContext: undefined
    }
  | {
      isTeamsResolved: true
      isInTeams: true
      teamsContext: app.Context
    }
  | {
      isTeamsResolved: true
      isInTeams: false
      teamsContext: undefined
    }

const provisionalTeamsState: TeamsContextState = {
  isTeamsResolved: false,
  isInTeams: false,
  teamsContext: undefined,
}

const TeamsContext = createContext<TeamsContextState | null>(null)
const teamsAppInitializationTimeout = 2000

export const MsTeamsProvider = ({
  shouldHoldUntilResolved,
  children,
}: {
  /**
   * Render nothing until detection settles. The root layout sets this when the
   * request looks like it comes from Teams, so the Teams layout never flashes
   * the web layout first; everyone else gets server-rendered children.
   */
  shouldHoldUntilResolved: boolean
  children: React.ReactNode
}) => {
  const [teamsState, setTeamsState] = useState<TeamsContextState>(
    provisionalTeamsState
  )

  useEffect(() => {
    if (!teamsState.isTeamsResolved) return
    const isLocalhost = window.location.host.startsWith("localhost")
    Cookies.set("isInTeams", teamsState.isInTeams ? "true" : "false", {
      // Persistent so the server-side hold survives a cold start of Teams
      // mobile, which drops session cookies with the app process; the cookie
      // is the only server-side Teams signal. Rewritten on every load.
      expires: 365,
      sameSite: isLocalhost ? "strict" : "none", // For MS Teams to work
      secure: !(window.settings.ALLOW_INSECURE_COOKIES && isLocalhost),
    })
  }, [teamsState])

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Outside any frame or popup app.initialize() rejects immediately; the
        // timeout bounds the wait when a parent or opener never answers.
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error("MS Teams initialization timeout"))
          }, teamsAppInitializationTimeout)
        })

        const context = await Promise.race([
          (async () => {
            await app.initialize()
            return await app.getContext()
          })(),
          timeoutPromise,
        ])

        setTeamsState({
          isTeamsResolved: true,
          teamsContext: context,
          isInTeams: true,
        })
      } catch (error) {
        // Not in Teams environment or timeout occurred, continue without Teams context
        if (window.settings.EMULATE_MS_TEAMS) {
          setTeamsState({
            isTeamsResolved: true,
            isInTeams: true,
            teamsContext: {
              page: { frameContext: FrameContexts.content },
            } as app.Context,
          })
        } else {
          setTeamsState({
            isTeamsResolved: true,
            isInTeams: false,
            teamsContext: undefined,
          })
        }
      }
    }

    void initializeTeams()
  }, []) // Empty dependency array means this runs once on mount

  if (shouldHoldUntilResolved && !teamsState.isTeamsResolved) {
    return null
  }

  return <TeamsContext value={teamsState}>{children}</TeamsContext>
}

export const useTeams = () => {
  const context = use(TeamsContext)
  if (!context) {
    throw new Error("useTeams must be used within a MsTeamsProvider")
  }
  return context
}
